import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { ChatConversation } from '../../interfaces/chat.interface'
import { handleAddAgentReportEntity } from './chat-agent-report-reducers.helper'
import { handleCreateContentStreamEntity } from './chat-content-stream-reducers.helper'
import {
  handleStartDelegation,
  type StartDelegationPayload,
} from './chat-delegation-reducers.helper'
import type {
  AddAgentReportEntityPayload,
  AddOptimisticMessagePayload,
  AddSubAgentChatMessagePayload,
  AppendMessageStreamContentPayload,
  AppendStreamPayload,
  CompleteMessageStreamingPayload,
  ConfirmOptimisticMessagePayload,
  CreateContentStreamEntityPayload,
  FailMessageStreamingPayload,
  FailStreamingPayload,
  IncrementPagePayload,
  RemoveOptimisticMessagePayload,
  StartMessageStreamingPayload,
  SyncMessagesFromApiPayload,
} from './chat-message-entity.interface'
import {
  handleAddOptimisticMessage,
  handleAppendMessageStreamContent,
  handleCompleteMessageStreaming,
  handleConfirmOptimisticMessage,
  handleFailMessageStreaming,
  handleRemoveOptimisticMessage,
  handleStartMessageStreaming,
} from './chat-message-reducers.helper'
import { createInitialChatState } from './chat-state.interface'
import {
  type CompleteAllConversationEntityStreamingPayload,
  type FinalizeEntityStreamingPayload,
  handleAppendToItemStream,
  handleClearConversationStreaming,
  handleCompleteAllConversationEntityStreaming,
  handleFailItemStreaming,
  handleFinalizeEntityStreaming,
} from './chat-streaming-reducers.helper'
import { handleAddSubAgentChatMessage } from './chat-sub-agent-chat-message.helper'
import { handleSyncMessagesFromApi } from './chat-sync-reducer.helper'
import {
  type AddToolCallEntityPayload,
  handleAddToolCallEntity,
  handleUpdateToolCallEntity,
  handleUpdateToolCallEntityProgress,
  type UpdateToolCallEntityPayload,
  type UpdateToolCallEntityProgressPayload,
} from './chat-tool-call-entity-reducers.helper'

export type { ChatState } from './chat-state.interface'

const initialState = createInitialChatState()

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveConversation: (
      state,
      action: PayloadAction<{ id: string; conversation?: ChatConversation } | null>
    ) => {
      if (action.payload === null) {
        state.activeConversationId = null
        state.activeConversation = null
        return
      }
      state.activeConversationId = action.payload.id
      state.activeConversation = action.payload.conversation ?? null
    },
    addOptimisticMessage: (state, action: PayloadAction<AddOptimisticMessagePayload>) =>
      handleAddOptimisticMessage(state, action.payload),
    confirmOptimisticMessage: (state, action: PayloadAction<ConfirmOptimisticMessagePayload>) =>
      handleConfirmOptimisticMessage(state, action.payload),
    removeOptimisticMessage: (state, action: PayloadAction<RemoveOptimisticMessagePayload>) =>
      handleRemoveOptimisticMessage(state, action.payload),
    startMessageStreaming: (state, action: PayloadAction<StartMessageStreamingPayload>) =>
      handleStartMessageStreaming(state, action.payload),
    appendMessageStreamContent: (state, action: PayloadAction<AppendMessageStreamContentPayload>) =>
      handleAppendMessageStreamContent(state, action.payload),
    completeMessageStreaming: (state, action: PayloadAction<CompleteMessageStreamingPayload>) =>
      handleCompleteMessageStreaming(state, action.payload),
    failMessageStreaming: (state, action: PayloadAction<FailMessageStreamingPayload>) =>
      handleFailMessageStreaming(state, action.payload),
    syncMessagesFromApi: (state, action: PayloadAction<SyncMessagesFromApiPayload>) =>
      handleSyncMessagesFromApi(state, action.payload),
    incrementPage: (state, action: PayloadAction<IncrementPagePayload>) => {
      const { conversationId } = action.payload
      if (!state.paginationByConversation[conversationId]) {
        state.paginationByConversation[conversationId] = { page: 1, hasMore: false }
      }
      state.paginationByConversation[conversationId].page += 1
    },
    advancePageTo: (state, action: PayloadAction<{ conversationId: string; page: number }>) => {
      const { conversationId, page } = action.payload
      const current = state.paginationByConversation[conversationId] ?? {
        page: 1,
        hasMore: false,
      }
      state.paginationByConversation[conversationId] =
        page > current.page ? { ...current, page } : current
    },
    resetPagination: (state, action: PayloadAction<{ conversationId: string }>) => {
      state.paginationByConversation[action.payload.conversationId] = { page: 1, hasMore: false }
    },
    appendToItemStream: (state, action: PayloadAction<AppendStreamPayload>) =>
      handleAppendToItemStream(state, action.payload),
    failItemStreaming: (state, action: PayloadAction<FailStreamingPayload>) =>
      handleFailItemStreaming(state, action.payload),
    clearConversationStreaming: (
      state,
      action: PayloadAction<CompleteAllConversationEntityStreamingPayload>
    ) => handleClearConversationStreaming(state, action.payload),
    completeAllConversationEntityStreaming: (
      state,
      action: PayloadAction<CompleteAllConversationEntityStreamingPayload>
    ) => handleCompleteAllConversationEntityStreaming(state, action.payload),
    finalizeEntityStreaming: (state, action: PayloadAction<FinalizeEntityStreamingPayload>) =>
      handleFinalizeEntityStreaming(state, action.payload),
    setPendingMessage: (state, action: PayloadAction<string>) => {
      state.pendingMessage = action.payload
    },
    clearConversation: (state, action: PayloadAction<{ conversationId: string }>) => {
      const idList = state.messageIdsByConversation[action.payload.conversationId] ?? []
      idList.forEach((id) => {
        delete state.messageEntities[id]
      })
      delete state.messageIdsByConversation[action.payload.conversationId]
      delete state.paginationByConversation[action.payload.conversationId]
      delete state.locallyCreatedConversationIds[action.payload.conversationId]
      delete state.isOrchestrationActiveByConversationId[action.payload.conversationId]
    },
    movePendingToConversation: (state, action: PayloadAction<{ targetConversationId: string }>) => {
      const pendingIdList = state.messageIdsByConversation.pending ?? []
      if (pendingIdList.length === 0) return
      pendingIdList.forEach((id) => {
        const message = state.messageEntities[id]
        if (message) {
          message.conversationId = action.payload.targetConversationId
          message.isPending = false
        }
      })
      if (!state.messageIdsByConversation[action.payload.targetConversationId]) {
        state.messageIdsByConversation[action.payload.targetConversationId] = []
      }
      state.messageIdsByConversation[action.payload.targetConversationId] = [
        ...pendingIdList,
        ...state.messageIdsByConversation[action.payload.targetConversationId],
      ]
      delete state.messageIdsByConversation.pending
    },
    addToolCallEntity: (state, action: PayloadAction<AddToolCallEntityPayload>) =>
      handleAddToolCallEntity(state, action.payload),
    updateToolCallEntity: (state, action: PayloadAction<UpdateToolCallEntityPayload>) =>
      handleUpdateToolCallEntity(state, action.payload),
    updateToolCallEntityProgress: (
      state,
      action: PayloadAction<UpdateToolCallEntityProgressPayload>
    ) => handleUpdateToolCallEntityProgress(state, action.payload),
    markConversationAsLocallyCreated: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      state.locallyCreatedConversationIds[action.payload.conversationId] = true
    },
    clearLocallyCreatedFlag: (state, action: PayloadAction<{ conversationId: string }>) => {
      delete state.locallyCreatedConversationIds[action.payload.conversationId]
    },
    setConversationStopping: (state, action: PayloadAction<{ conversationId: string }>) => {
      state.isStoppingByConversationId[action.payload.conversationId] = true
    },
    clearConversationStopping: (state, action: PayloadAction<{ conversationId: string }>) => {
      delete state.isStoppingByConversationId[action.payload.conversationId]
    },
    setOrchestrationActive: (state, action: PayloadAction<{ conversationId: string }>) => {
      state.isOrchestrationActiveByConversationId[action.payload.conversationId] = true
    },
    clearOrchestrationActive: (state, action: PayloadAction<{ conversationId: string }>) => {
      delete state.isOrchestrationActiveByConversationId[action.payload.conversationId]
    },
    confirmAllPendingInConversation: (state, action: PayloadAction<{ conversationId: string }>) => {
      const idList = state.messageIdsByConversation[action.payload.conversationId] ?? []
      idList.forEach((id) => {
        const message = state.messageEntities[id]
        if (message?.isPending) message.isPending = false
      })
    },
    startDelegation: (state, action: PayloadAction<StartDelegationPayload>) =>
      handleStartDelegation(state, action.payload),
    addAgentReportEntity: (state, action: PayloadAction<AddAgentReportEntityPayload>) =>
      handleAddAgentReportEntity(state, action.payload),
    addSubAgentChatMessage: (state, action: PayloadAction<AddSubAgentChatMessagePayload>) =>
      handleAddSubAgentChatMessage(state, action.payload),
    createContentStreamEntity: (state, action: PayloadAction<CreateContentStreamEntityPayload>) =>
      handleCreateContentStreamEntity(state, action.payload),
    resetChat: () => createInitialChatState(),
  },
})

export const {
  setActiveConversation,
  addOptimisticMessage,
  confirmOptimisticMessage,
  removeOptimisticMessage,
  startMessageStreaming,
  appendMessageStreamContent,
  completeMessageStreaming,
  failMessageStreaming,
  syncMessagesFromApi,
  incrementPage,
  advancePageTo,
  resetPagination,
  appendToItemStream,
  failItemStreaming,
  clearConversationStreaming,
  completeAllConversationEntityStreaming,
  finalizeEntityStreaming,
  setPendingMessage,
  clearConversation,
  movePendingToConversation,
  addToolCallEntity,
  updateToolCallEntity,
  updateToolCallEntityProgress,
  markConversationAsLocallyCreated,
  clearLocallyCreatedFlag,
  setConversationStopping,
  clearConversationStopping,
  setOrchestrationActive,
  clearOrchestrationActive,
  confirmAllPendingInConversation,
  startDelegation,
  addAgentReportEntity,
  addSubAgentChatMessage,
  createContentStreamEntity,
  resetChat,
} = chatSlice.actions

export const chatReducer = chatSlice.reducer
